import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { ValidationItems } from '@kanda-libs/ks-component-ts';

import { formatToCurrency } from 'utils';

import { DepositSelectButtonProps } from './DepositSelectButton';
import { GRID_CLASSNAMES } from './constants';
import { calculateRateDepositLimits, determineValidDeposits } from './helpers';

import { FinanceFormValues, useFinanceFormContext } from '../../../context';

export interface DepositSelectHook {
  gridClassName: string;
  buttons: DepositSelectButtonProps[];
  showCustomField: boolean;
  customValidation: ValidationItems;
  customDepositTtitle: string;
  onBlur: () => void;
}

export default function useDepositSelect(): DepositSelectHook {
  const { job, allRates } = useFinanceFormContext();
  const { setValue, trigger } = useFormContext<FinanceFormValues>();

  const [apr, aprType, duration, deferredDuration, deposit, depositPercent] =
    useWatch<
      FinanceFormValues,
      [
        'apr',
        'aprType',
        'duration',
        'deferredDuration',
        'deposit',
        'depositPercent',
      ]
    >({
      name: [
        'apr',
        'aprType',
        'duration',
        'deferredDuration',
        'deposit',
        'depositPercent',
      ],
    });

  const currentRates = useMemo(() => {
    if (aprType === 'BUYNOW_PAYLATER') {
      if (!duration || !deferredDuration) return [];
      const aprKey = `${apr}-${aprType}`;
      const durationKey = `${deferredDuration}-${duration}`;
      return allRates?.[aprKey]?.[durationKey];
    }
    const aprKey = `${apr}-${aprType}`;
    const durationKey = `${duration}`;
    return allRates?.[aprKey]?.[durationKey];
  }, [apr, aprType, duration, deferredDuration, allRates]);

  const total = useMemo(
    () => job.total?.amount_vat_inclusive?.amount ?? 0,
    [job],
  );

  const depositAmounts = useMemo(
    () => determineValidDeposits(total, currentRates),
    [total, currentRates],
  );

  const depositButtonBases = useMemo(
    () => [...depositAmounts, { percentage: 'custom', value: -1 }],
    [depositAmounts],
  );

  const gridBaseClassName = useMemo(() => {
    if (!depositButtonBases) return GRID_CLASSNAMES[1];
    if (depositButtonBases.length > 3) return GRID_CLASSNAMES[4];
    return GRID_CLASSNAMES[depositButtonBases.length];
  }, [depositButtonBases]);

  const gridClassName = useMemo(
    () =>
      ['gap-x-2 md:gap-x-3 gap-y-2 md:gap-y-3', gridBaseClassName].join(' '),
    [gridBaseClassName],
  );

  const handleClick = useCallback(
    (value: number, percentage: string) => {
      if (percentage === 'custom') {
        setValue('deposit', undefined);
        setValue('depositPercent', percentage);
        return;
      }
      setValue('deposit', value);
      setValue('depositPercent', percentage);
    },
    [setValue],
  );

  const buttons: DepositSelectButtonProps[] = useMemo(
    () =>
      depositButtonBases.map(({ value, percentage }) => ({
        onClick: () => handleClick(value, percentage),
        value,
        percentage,
        id: `deposit-${percentage}`,
        key: `deposit-${percentage}`,
      })),
    [handleClick, depositButtonBases],
  );

  const showCustomField = useMemo(
    () => depositPercent === 'custom',
    [depositPercent],
  );

  const limits = useMemo(
    () =>
      currentRates.reduce(
        (final: number[], rate) => {
          const rateLimits = calculateRateDepositLimits(total, rate);
          return [
            Math.min(final[0], rateLimits[0]),
            Math.max(final[1], rateLimits[1]),
          ];
        },
        [Infinity, -Infinity],
      ),
    [currentRates, total],
  );

  const customDepositTtitle = useMemo(
    () =>
      `Enter a deposit between ${formatToCurrency(
        limits[0],
      )} and ${formatToCurrency(limits[1])}`,
    [limits],
  );

  const customValidation = useMemo(
    () => ({
      required: { value: true, message: 'Amount is required.' },
      min: {
        value: limits[0],
        message: `Deposit must be equal to or greater than ${formatToCurrency(
          limits[0],
        )}`,
      },
      max: {
        value: limits[1],
        message: `Deposit must be equal to or less than ${formatToCurrency(
          limits[1],
        )}`,
      },
    }),
    [limits],
  );

  const onBlur = useCallback(() => trigger('deposit'), [trigger]);

  useEffect(() => {
    if (depositAmounts.length === 0) {
      if (deposit === undefined) return;
      setValue('deposit', undefined);
      setValue('depositPercent', undefined);
      return;
    }
    if (depositPercent === 'custom') {
      // const sorted = depositAmounts.sort((a, b) => a.value - b.value);
      // const min = sorted[0];
      // const max = sorted[sorted.length - 1];
      // if (deposit >= min.value && deposit <= max.value) return;
      // setValue('deposit', min.value);
      // setValue('depositPercent', min.percentage);
      return;
    }
    if (!deposit) {
      const min = depositAmounts.sort((a, b) => a.value - b.value)[0];
      setValue('deposit', min.value);
      setValue('depositPercent', min.percentage);
      return;
    }
    const inValues = depositAmounts.some(
      (v) => v.value === deposit && v.percentage === depositPercent,
    );
    if (inValues) return;
    const min = depositAmounts.sort((a, b) => a.value - b.value)[0];
    setValue('deposit', min.value);
    setValue('depositPercent', min.percentage);
  }, [depositAmounts, deposit, depositPercent, setValue]);

  return {
    gridClassName,
    buttons,
    showCustomField,
    customValidation,
    customDepositTtitle,
    onBlur,
  };
}
